// sharepoint.types.ts - Complete types file with exclusion-based hierarchical document selection

import { sharepointTabConfig } from "../constants/sharepointTabConfig";

// Core SharePoint types
export interface SharePointItem {
  id: string;
  title: string;
  name: string;
  display_name: string;
  list_id?: string;
  library_id?: string;
  drive_id?: string;
  isChecked: boolean;
  checked: boolean;
  created: Date;
  modified: Date;
  [key: string]: any; // Allow additional dynamic properties
}

// Interface for individual list/library items (dynamic fields)
export interface SharePointListItem {
  id: string;
  name?: string;
  title?: string;
  is_folder?: boolean;
  file_type?: string;
  children?: SharePointListItem[];
  fields?: Record<string, any>;
  [key: string]: any; // Dynamic fields - can contain any field from SharePoint
}

// UPDATED: NextLink-based pagination info interface (removed page numbers)
export interface PaginationInfo {
  pageSize: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  nextLink?: string;
  prevLink?: string;
  itemCount: number; // Number of items in current batch
  canGoBack: boolean; // Can navigate backwards
  canGoForward: boolean; // Can navigate forwards
}

// UPDATED: Enhanced drill-down state with nextLink-based pagination
export interface DrillDownState {
  isViewingItems: boolean;
  selectedItem: SharePointItem | null;
  itemType: 'lists' | 'document_libraries';
  items: SharePointListItem[];
  isLoading: boolean;
  error?: string;
  // NextLink-based pagination state
  pagination: PaginationInfo;
  // Loading state for navigation changes
  isLoadingPage: boolean;
  // Current filters applied
  appliedFilters?: Record<string, any>;
  // Navigation history for back button
  navigationHistory: string[]; // Array of nextLink tokens
  currentHistoryIndex: number; // Current position in history
}

// UPDATED: Exclusion-based structure (like Canvas)
export interface ExcludedItems {
  excluded_lists: string[];
  excluded_document_libraries: string[]; // Completely excluded libraries
  partial_excluded_document_libraries: Array<{
    lib_id: string;
    excluded_docs: string[]; // folders + documents IDs
  }>;
}

export interface SelectedItems extends Record<string, boolean> {
  lists: boolean;
  document_libraries: boolean;
}

export interface Visibility {
  lists: boolean;
  document_libraries: boolean;
}

export interface SharePointItems {
  lists: SharePointItem[];
  document_libraries: SharePointItem[];
  [key: string]: SharePointItem[]; // This line fixes the indexing error
}

export interface SharePointData {
  id: number;
  sharepoint_id: string;
  url: string;
  order: number;
  sharepoint_name: string;
  activeTab: 'CONNECT' | 'PREVIEW';
  connectionState: 'idle' | 'connecting' | 'connected';
  selectedItems: SelectedItems;
  excludedItems: ExcludedItems;
  visibility: Visibility;
  sharepointItems: SharePointItems;
  isLoadingItems: boolean;
  error?: string;
  UrlError?: string;
  tenant?: string;
  drillDownState?: DrillDownState;
  site_name?: string;  // ADD THIS
  
  // NEW: Add these URL targeting fields
  url_type?: 'site' | 'list' | 'library' | 'folder';
  target_list_id?: string;
  target_library_id?: string;
  target_folder_path?: string;
  original_url?: string;
  connection_method?: string;
  url_analysis?: {
    url_type: 'site' | 'list' | 'library' | 'folder';
    target_list_id?: string;
    target_library_id?: string;
    target_folder_path?: string;
  };
}

// UPDATED: API Response types for nextLink pagination
export interface PaginatedResponse<T> {
  items: T[];
  nextLink?: string;
  prevLink?: string;
  pageSize: number;
  hasMore: boolean;
  count: number;
  filters_applied?: number;
}

export interface ConnectionResponse {
  message: string;
  data: {
    id: string;
    name: string;
  };
  connection_status: 'success' | 'failure';
  tenant: string;
}

// UI Component types
export interface ContextMenu {
  visible: boolean;
  x: number;
  y: number;
  sharepointId: number;
}

export interface CurrentStatus {
  progress: number;
  status: string;
  error?: string;
}

export interface CustomModalState {
  isOpen: boolean;
  title: string;
  message: string;
  type: "success" | "error" | "loading" | "confirmation" | "warning";
  onConfirm?: () => void;
}

// System Configuration types
export interface SystemConfig {
  Id: string;
  ConfigValue: string;
}

// User types
export interface User {
  id: string;
  name: string;
  email: string;
}

// Pipeline and Status types
export interface PipelineStatus {
  progress: number;
  status: 'In Progress' | 'Complete' | 'Failed' | '';
  error?: string;
}

// Visibility key type for toggle switches
export type VisibilityKey = 'lists' | 'document_libraries';

// NEW: Document selection state types
export type DocumentSelectionState = 'checked' | 'unchecked' | 'indeterminate';

export interface LibrarySelectionInfo {
  state: DocumentSelectionState;
  totalItems: number;
  selectedItems: number;
}

// Component Props types
export interface SharePointHeaderProps {
  systemConfig: SystemConfig[];
  onClose: () => void;
}

export interface SharePointSidebarProps {
  sharepoints: SharePointData[];
  setSharepoints: React.Dispatch<React.SetStateAction<SharePointData[]>>;
  selectedSharePoint: number;
  setSelectedSharePoint: (id: number) => void;
  systemConfig: SystemConfig[];
  contextMenu: ContextMenu;
  setContextMenu: React.Dispatch<React.SetStateAction<ContextMenu>>;
  onAddNewSharePoint: () => void;
  onMoveSharePoint: (sharepointId: number, direction: 'up' | 'down') => void;
  onDeleteSharePoint: (sharepointId: number) => void;
  onNameChange: (event: React.ChangeEvent<HTMLInputElement>, sharepointId: number) => void;
  onDragStart: (e: React.DragEvent, position: number) => void;
  onDragEnter: (e: React.DragEvent, position: number) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onContextMenu: (e: React.MouseEvent, sharepointId: number) => void;
}

export interface SharePointConnectionTabProps {
  selectedSharepointData: SharePointData | undefined;
  onUrlChange: (value: string) => void;
  onConnect: (sharepointId: number, source: 'click' | 'loop') => void;
  systemConfig: SystemConfig[];
  assistant: any;
  showHowImage: boolean;
  setShowHowImage: (show: boolean) => void;
  imageSrc: string;
  setImageSrc: (src: string) => void;
  modalSize: string;
  setModalSize: (size: string) => void;
}

export interface SharePointPreviewTabProps {
  selectedSharepointData: SharePointData;
  activeSubTabs: keyof typeof sharepointTabConfig;
  setActiveSubTabs: (tab: keyof typeof sharepointTabConfig) => void;
  checkedItems: string[];
  isLoadingTabs: { [sharepointId: number]: { [key: string]: boolean } };
  showDocumentCache: boolean;
  isRefreshing: boolean;
  onRefresh: () => void;
  onCheckboxChange: (itemId: string, itemType: string, sharepointId: number, idField: string) => void;
  onToggleChange: (key: VisibilityKey, sharepointId: number) => void;
  updateExcludedItems: (sharepointId: number, type: keyof ExcludedItems, itemId: string) => void;
  onItemClick: (item: SharePointItem, itemType: 'lists' | 'document_libraries', sharepointId: number) => void;
  onBackToLists: (sharepointId: number) => void;
  onNavigateNext: (sharepointId: number) => Promise<void>;
  onNavigatePrevious: (sharepointId: number) => Promise<void>;
  onPageSizeChange: (sharepointId: number, pageSize: number) => Promise<void>;
  // ADD THESE NEW PROPS:
  onDocumentExclusion?: (sharepointId: number, documentId: string, libraryId: string, exclude: boolean) => void;
  onLibraryExclusion?: (sharepointId: number, libraryId: string, exclude: boolean) => void;
  isDocumentExcluded?: (documentId: string, libraryId: string, parentIds: string[], sharepoint: SharePointData) => boolean;
  getLibrarySelectionState?: (libraryId: string, sharepoint: SharePointData) => DocumentSelectionState;
}

export interface SharePointItemsViewProps {
  sharepointId: number;
  items: SharePointListItem[];
  isLoading: boolean;
  error?: string;
  selectedItem: SharePointItem | null;
  itemType: 'lists' | 'document_libraries';
  onBack: () => void;
  pagination: PaginationInfo;
  isLoadingPage: boolean;
  onNavigateNext: () => Promise<void>;
  onNavigatePrevious: () => Promise<void>;
  onPageSizeChange: (pageSize: number) => Promise<void>;
  onSearch?: (searchTerm: string) => void;
  appliedFilters?: Record<string, any>;
  // REPLACE documentSelections WITH:
  onDocumentExclusion?: (sharepointId: number, documentId: string, libraryId: string, exclude: boolean) => void;
  onLibraryExclusion?: (sharepointId: number, libraryId: string, exclude: boolean) => void;
  isDocumentExcluded?: (documentId: string, libraryId: string, parentIds: string[], sharepoint: SharePointData) => boolean;
  sharepoint: SharePointData; 
}

export interface SharePointTabProps {
  activeSubTab: keyof typeof sharepointTabConfig;
  displayName: string;
  matchSubTab: string;
  visibility: Record<string, boolean>;
  items: any[];
  itemCount: number;
  handleTabChange: (key: string) => void;
  handleCheckboxChange: (id: string, type: string, sharepointId: number, idField: string) => void;
  checkedItems: string[];
  sharepointId: number;
  excludedItems: ExcludedItems;
  updateExcludedItems: (sharepointId: number, type: keyof ExcludedItems, itemId: string) => void;
  selectedItems: Record<string, boolean>;
  drillDownState?: DrillDownState;
  onItemClick?: (item: SharePointItem, itemType: 'lists' | 'document_libraries') => void;
  onBackToLists?: () => void;
  onNavigateNext?: () => Promise<void>;
  onNavigatePrevious?: () => Promise<void>;
  onPageSizeChange?: (pageSize: number) => Promise<void>;
  // ADD THESE NEW PROPS:
  onDocumentExclusion?: (sharepointId: number, documentId: string, libraryId: string, exclude: boolean) => void;
  onLibraryExclusion?: (sharepointId: number, libraryId: string, exclude: boolean) => void;
  isDocumentExcluded?: (documentId: string, libraryId: string, parentIds: string[], sharepoint: SharePointData) => boolean;
  getLibrarySelectionState?: (libraryId: string, sharepoint: SharePointData) => DocumentSelectionState;
  sharepoint: SharePointData; // ← ADD THIS LINE
  searchTerm?: string; // ← ADD THIS NEW LINE
}


export interface SharePointFooterProps {
  sharepoints: SharePointData[];
  isSharePointRNightly: boolean;
  setIsSharePointRNightly: React.Dispatch<React.SetStateAction<boolean>>;
  currentStatus: CurrentStatus | null;
  pipelineData: any[];
  expandedGetData: boolean;
  setExpandedGetData: (expanded: boolean) => void;
  expandedRows: Record<number, boolean>;
  toggleExpandRow: (idx: number) => void;
  pendingSave: boolean;
  gettingData: boolean;
  lastSaveTarget: 'azure' | 'azure';
  assistant: any;
  onRefreshNightlyChange: () => void;
  onGetDataNow: (type: 'azure' | 'azure') => Promise<void>;
}

export interface SharePointModalsProps {
  customModal: CustomModalState;
  setCustomModal: React.Dispatch<React.SetStateAction<CustomModalState>>;
  modalRefreshNModal: boolean;
  setModalRefreshNModal: (open: boolean) => void;
}

// Main SharePoint component props
export interface SharePointProps {
  isSharePointModalOpen: boolean;
  setSharePointModalOpen: (isOpen: boolean) => void;
  sharepoints: SharePointData[];
  setSharepoints: React.Dispatch<React.SetStateAction<SharePointData[]>>;
  updateExcludedItems: (sharepointId: number, type: keyof ExcludedItems, itemId: string) => void;
  handleToggleChange: (key: VisibilityKey, sharepointId: number) => void;
  assistant: any;
  triggerAutoSave: () => void;
  isSharePointRNightly: boolean;
  setIsSharePointRNightly: React.Dispatch<React.SetStateAction<boolean>>;
  isSharePointFullLoad: boolean;
  setIsSharePointFullLoad: React.Dispatch<React.SetStateAction<boolean>>;
  // REMOVE THIS LINE:
  // onDocumentSelectionChange?: (sharepointId: number, documentId: string, libraryId: string, selected: boolean) => void;
}

// Utility types for drag and drop
export interface DragRefs {
  dragItem: React.MutableRefObject<number | null>;
  dragOverItem: React.MutableRefObject<number | null>;
}

// API Function types with nextLink pagination
export type FetchFunction = (
  sharepoint_id: string,
  sharepoint_url: string,
  app_id: string,
  isRefresh?: boolean
) => Promise<any>;

export type FetchItemsFunction = (
  sharepoint_id: string,
  list_or_drive_id: string,
  app_id: string,
  limit?: number
) => Promise<SharePointListItem[]>;

export type FetchItemsPaginatedFunction = (
  sharepoint_id: string,
  list_or_drive_id: string,
  filters?: Record<string, any>,
  pageSize?: number,
  nextLink?: string
) => Promise<PaginatedResponse<SharePointListItem>>;

// Loading states
export interface LoadingStates {
  [sharepointId: number]: {
    [key: string]: boolean;
  };
}

// History and Pipeline types
export interface HistoryItem {
  date: string;
  status: 'Success' | 'Failed';
  duration: string;
  document_processed?: string;
  successfull?: number;
  failed?: number;
  failedDocuments?: string[];
  error?: string;
}

// Cache utilities
export interface CacheItem {
  value: any;
  timestamp: number;
}

// Event handler types with nextLink pagination
export type SharePointEventHandler<T = void> = (sharepointId: number) => T;
export type SharePointAsyncEventHandler<T = void> = (sharepointId: number) => Promise<T>;
export type DragEventHandler = (e: React.DragEvent, position: number) => void;
export type ContextMenuEventHandler = (e: React.MouseEvent, sharepointId: number) => void;

export type ItemClickHandler = (item: SharePointItem, itemType: 'lists' | 'document_libraries', sharepointId: number) => void;
export type BackNavigationHandler = (sharepointId: number) => void;
export type NextNavigationHandler = (sharepointId: number) => Promise<void>;
export type PreviousNavigationHandler = (sharepointId: number) => Promise<void>;
export type PageSizeChangeHandler = (sharepointId: number, pageSize: number) => Promise<void>;

// Filter and Search types
export interface FilterConfig {
  field: string;
  operator: 'eq' | 'ne' | 'gt' | 'ge' | 'lt' | 'le' | 'contains' | 'startswith' | 'endswith';
  value: any;
}

export interface SearchConfig {
  searchTerm: string;
  searchFields?: string[];
}

export interface SortConfig {
  field: string;
  direction: 'asc' | 'desc';
}

export interface ListItemsRequest {
  sharepoint_id: string;
  list_id: string;
  filters?: Record<string, any>;
  pageSize?: number;
  nextLink?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface DriveItemsRequest {
  sharepoint_id: string;
  drive_id: string;
  pageSize?: number;
  nextLink?: string;
}

// Default pagination values for nextLink approach
export const DEFAULT_PAGINATION: PaginationInfo = {
  pageSize: 10,
  hasNextPage: false,
  hasPrevPage: false,
  nextLink: undefined,
  prevLink: undefined,
  itemCount: 0,
  canGoBack: false,
  canGoForward: false
};

// Default drill-down state with nextLink pagination
export const DEFAULT_DRILL_DOWN_STATE: DrillDownState = {
  isViewingItems: false,
  selectedItem: null,
  itemType: 'lists',
  items: [],
  isLoading: false,
  error: undefined,
  pagination: DEFAULT_PAGINATION,
  isLoadingPage: false,
  appliedFilters: undefined,
  navigationHistory: [],
  currentHistoryIndex: -1
};

// Helper type for component state updates
export type SharePointStateUpdater = React.Dispatch<React.SetStateAction<SharePointData[]>>;

// Helper type for safe null checks
export type Maybe<T> = T | null | undefined;

// Utility type for required nextLink pagination props
export type RequiredPaginationProps = {
  pagination: PaginationInfo;
  isLoadingPage: boolean;
  onNavigateNext: () => Promise<void>;
  onNavigatePrevious: () => Promise<void>;
  onPageSizeChange: (pageSize: number) => Promise<void>;
};

// Export commonly used types as a group
export type SharePointCoreTypes = {
  SharePointData: SharePointData;
  SharePointItem: SharePointItem;
  SharePointListItem: SharePointListItem;
  DrillDownState: DrillDownState;
  PaginationInfo: PaginationInfo;
};